import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Plane, ArrowLeft } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import AppLogo from '@/components/AppLogo';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <SEOHead
        title="Privacy Policy"
        description="How How Busy Is My Airport collects, uses, and protects your data. We don't sell your information and use minimal analytics."
        path="/privacy"
      />
      {/* Header */}
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <div className="leading-tight min-w-0">
              <h1 className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900 leading-[1.15]">
                How Busy Is My Airport
              </h1>
            </div>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </motion.div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-slate-800 mb-2 text-center">Privacy Policy</h1>
            <p className="text-slate-500 text-center mb-8">Last updated: February 2026</p>
          </motion.div>

          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-8 prose prose-slate max-w-none">
              
              <h2 className="text-xl font-semibold text-slate-800 mt-0">1. Introduction</h2>
              <p className="text-slate-600">
                Welcome to Airport Busyness Predictor ("we," "our," or "us"). We respect your privacy and are 
                committed to protecting your personal data. This privacy policy explains how we collect, use, 
                and safeguard your information when you use our website and services.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">2. Information We Collect</h2>
              <p className="text-slate-600">We collect the following types of information:</p>
              <ul className="text-slate-600">
                <li>
                  <strong>Usage Data:</strong> Information about how you use our website, including airports 
                  searched, dates/times selected, and features used.
                </li>
                <li>
                  <strong>Device Information:</strong> Browser type, operating system, device type, and IP address.
                </li>
                <li>
                  <strong>Feedback Data:</strong> When you provide feedback on prediction accuracy, we store 
                  the prediction details and your feedback response.
                </li>
                <li>
                  <strong>Cookies:</strong> We use cookies and similar technologies for analytics and to 
                  improve your experience.
                </li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-800">3. How We Use Your Information</h2>
              <p className="text-slate-600">We use the information we collect to:</p>
              <ul className="text-slate-600">
                <li>Provide and improve our airport busyness prediction service</li>
                <li>Analyze usage patterns to enhance user experience</li>
                <li>Improve prediction accuracy based on user feedback</li>
                <li>Display relevant advertisements through Google AdSense</li>
                <li>Monitor and analyze website performance</li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-800">4. Third-Party Services</h2>
              <p className="text-slate-600">We use the following third-party services:</p>
              <ul className="text-slate-600">
                <li>
                  <strong>Google Analytics:</strong> To analyze website traffic and usage patterns. 
                  <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"> Google's Privacy Policy</a>
                </li>
                <li>
                  <strong>Google AdSense:</strong> To display advertisements. Google may use cookies to 
                  serve ads based on your visits to this and other websites.
                  <a href="https://policies.google.com/technologies/ads" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"> Google Ads Policy</a>
                </li>
                <li>
                  <strong>FlightAware:</strong> To obtain flight schedule data. We do not share your 
                  personal information with FlightAware.
                </li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-800">5. Cookies</h2>
              <p className="text-slate-600">
                We use cookies and similar tracking technologies to track activity on our website and store 
                certain information. You can instruct your browser to refuse all cookies or indicate when a 
                cookie is being sent. However, if you do not accept cookies, some portions of our service 
                may not function properly.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">6. Data Security</h2>
              <p className="text-slate-600">
                We implement appropriate technical and organizational security measures to protect your 
                personal data. However, no method of transmission over the Internet or electronic storage 
                is 100% secure, and we cannot guarantee absolute security.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">7. Data Retention</h2>
              <p className="text-slate-600">
                We retain your personal data only for as long as necessary to fulfill the purposes outlined 
                in this privacy policy. Usage data is typically retained for analytics purposes for up to 
                26 months.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">8. Your Rights</h2>
              <p className="text-slate-600">Depending on your location, you may have the right to:</p>
              <ul className="text-slate-600">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to or restrict processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>

              <h2 className="text-xl font-semibold text-slate-800">9. Children's Privacy</h2>
              <p className="text-slate-600">
                Our service is not intended for children under 13 years of age. We do not knowingly collect 
                personal information from children under 13. If you are a parent or guardian and believe 
                your child has provided us with personal information, please contact us.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">10. Changes to This Policy</h2>
              <p className="text-slate-600">
                We may update this privacy policy from time to time. We will notify you of any changes by 
                posting the new policy on this page and updating the "Last updated" date.
              </p>

              <h2 className="text-xl font-semibold text-slate-800">11. Contact Us</h2>
              <p className="text-slate-600">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul className="text-slate-600">
                <li>Email: <a href="mailto:jamursch@gmail.com" className="text-blue-600 hover:underline">jamursch@gmail.com</a></li>
                <li>Website: <a href="https://howbusyismyairport.com" className="text-blue-600 hover:underline">howbusyismyairport.com</a></li>
              </ul>

            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center gap-6 mb-4">
            <Link to="/" className="text-slate-600 hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/about" className="text-slate-600 hover:text-blue-600 transition-colors">About</Link>
            <Link to="/blog" className="text-slate-600 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/privacy" className="text-slate-600 hover:text-blue-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-slate-600 hover:text-blue-600 transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Airport Busyness Predictor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
